import { classifyMutation, type HapticKind } from './haptics-core.js';
import { emitHaptic } from './haptics';

export class ApiError extends Error {
  status: number;
  details?: Record<string, unknown>;

  constructor(message: string, status: number, details?: Record<string, unknown>) {
    super(message);
    this.status = status;
    this.details = details;
  }
}

const WORKSPACE_KEY = 'nosivo.workspace';

export function getWorkspaceSlug() {
  if (typeof window === 'undefined') return '';
  return String(window.localStorage.getItem(WORKSPACE_KEY) || '').trim().toLowerCase();
}

export function setWorkspaceSlug(value: string) {
  if (typeof window === 'undefined') return;
  const slug = String(value || '').trim().toLowerCase();
  if (slug) window.localStorage.setItem(WORKSPACE_KEY, slug);
  else window.localStorage.removeItem(WORKSPACE_KEY);
}

export async function api<T>(path: string, options: RequestInit = {}): Promise<T> {
  const workspace = getWorkspaceSlug();
  const method = String(options.method || 'GET').toUpperCase();
  let response: Response;
  try {
    response = await fetch(path, {
      credentials: 'same-origin',
      ...options,
      headers: {
        ...(options.body && !(options.body instanceof FormData) ? { 'Content-Type': 'application/json' } : {}),
        ...(workspace ? { 'X-Nosivo-Tenant': workspace } : {}),
        ...options.headers,
      },
    });
  } catch (error) {
    if (!['GET', 'HEAD', 'OPTIONS'].includes(method)) emitHaptic('error', { source: 'api-network' });
    throw error;
  }
  if (response.status === 204) {
    const kind = classifyMutation({ method, path, body: options.body, ok: true });
    if (kind) emitHaptic(kind as HapticKind, { source: 'api-success' });
    return undefined as T;
  }
  const isJson = response.headers.get('content-type')?.includes('application/json');
  const data = isJson ? await response.json() : null;
  if (!response.ok) {
    if (!['GET', 'HEAD', 'OPTIONS'].includes(method)) emitHaptic('error', { source: 'api-error' });
    throw new ApiError(data?.error || `Request failed (${response.status}).`, response.status, data?.details);
  }
  const kind = classifyMutation({ method, path, body: options.body, ok: true });
  if (kind) emitHaptic(kind as HapticKind, { source: 'api-success' });
  return data as T;
}

export function get<T>(path: string) {
  return api<T>(path);
}

export function post<T>(path: string, body?: unknown) {
  return api<T>(path, { method: 'POST', body: body === undefined ? undefined : JSON.stringify(body) });
}

export function patch<T>(path: string, body: unknown) {
  return api<T>(path, { method: 'PATCH', body: JSON.stringify(body) });
}

export function postForm<T>(path: string, body: FormData) {
  return api<T>(path, { method: 'POST', body });
}

export function remove<T = void>(path: string) {
  return api<T>(path, { method: 'DELETE' });
}

export function queryString(values: Record<string, string | number | null | undefined>) {
  const params = new URLSearchParams();
  Object.entries(values).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') params.set(key, String(value));
  });
  const result = params.toString();
  return result ? `?${result}` : '';
}
